import Vue from 'vue';
import VueI18n from 'vue-i18n';
import VueRouter from 'vue-router';
import axios from 'axios';
// Global Components
import hljs from 'highlight.js';
import Multiselect from 'vue-multiselect';
import VueFormulate from '@braid/vue-formulate';
import WootSwitch from 'components/ui/Switch';
import WootWizard from 'components/ui/Wizard';
import { sync } from 'vuex-router-sync';
import VTooltip from 'v-tooltip';
import WootUiKit from '../dashboard/components';
import App from '../dashboard/App';
import i18n from '../dashboard/i18n';
import createAxios from '../dashboard/helper/APIHelper';
import { emitter } from '../shared/helpers/mitt';

import commonHelpers, { isJSONValid } from '../dashboard/helper/commons';
import router, { initalizeRouter } from '../dashboard/routes';
import store from '../dashboard/store';
import constants from 'dashboard/constants/globals';
import * as Sentry from '@sentry/vue';
import 'vue-easytable/libs/theme-default/index.css';
import { Integrations } from '@sentry/tracing';
import {
  initializeAnalyticsEvents,
  initializeChatwootEvents,
} from '../dashboard/helper/scriptHelpers';
import FluentIcon from 'shared/components/FluentIcon/DashboardIcon';
import VueDOMPurifyHTML from 'vue-dompurify-html';
import { domPurifyConfig } from '../shared/helpers/HTMLSanitizer';
import AnalyticsPlugin from '../dashboard/helper/AnalyticsHelper/plugin';
import resizeDirective from '../dashboard/helper/directives/resize.js';
import { directive as onClickaway } from 'vue-clickaway';

Vue.config.env = process.env;

if (window.errorLoggingConfig) {
  Sentry.init({
    Vue,
    dsn: window.errorLoggingConfig,
    denyUrls: [
      // Chrome extensions
      /^chrome:\/\//i,
      /chrome-extension:/i,
      /extensions\//i,

      // Locally saved copies
      /file:\/\//i,

      // Safari extensions.
      /safari-web-extension:/i,
      /safari-extension:/i,
    ],
    integrations: [new Integrations.BrowserTracing()],
    ignoreErrors: [
      'ResizeObserver loop completed with undelivered notifications',
    ],
  });
}

Vue.use(VueDOMPurifyHTML, domPurifyConfig);
Vue.use(VueRouter);
Vue.use(VueI18n);
Vue.use(WootUiKit);
Vue.use(VueFormulate, {
  rules: {
    JSON: ({ value }) => isJSONValid(value),
  },
});
Vue.use(VTooltip, {
  defaultHtml: false,
  defaultContainer: 'body',
  defaultBoundariesElement: 'window',
  defaultPopperOptions: {
    modifiers: {
      preventOverflow: {
        boundariesElement: 'window',
        padding: 8,
      },
      flip: {
        boundariesElement: 'window',
      },
    },
  },
});
Vue.use(hljs.vuePlugin);
Vue.use(AnalyticsPlugin);

Vue.component('multiselect', Multiselect);
Vue.component('woot-switch', WootSwitch);
Vue.component('woot-wizard', WootWizard);
Vue.component('fluent-icon', FluentIcon);

Vue.directive('resize', resizeDirective);
Vue.directive('on-clickaway', onClickaway);
const i18nConfig = new VueI18n({
  locale: window.chatwootConfig?.selectedLocale || 'pt_BR',
  messages: i18n,
});

sync(store, router);
// load common helpers into js
commonHelpers();

window.WootConstants = constants;
window.axios = createAxios(axios);
Vue.prototype.$emitter = emitter;

initializeChatwootEvents();
initializeAnalyticsEvents();
initalizeRouter();

// --- Global error handlers ---
// Prevent "tela preta" by catching render errors and re-mounting when possible,
// and by reloading the page when a chunk fails to load after a deploy.

function isChunkLoadError(error) {
  return (
    error &&
    (error.name === 'ChunkLoadError' ||
      (typeof error.message === 'string' &&
        (error.message.includes('Loading chunk') ||
          error.message.includes('Loading CSS chunk'))))
  );
}

// Guarded reload to prevent infinite loop if the new deploy also 404s.
// The guard is time-boxed: without it the flag never expired, so the *second*
// deploy during the same tab session left the user stuck on a broken screen
// with no reload. A minute is enough to tell "the new build is broken too"
// (loop) from "another deploy happened later".
const RELOAD_GUARD_KEY = 'cc_reloaded_on_chunk_error';
const RELOAD_GUARD_WINDOW_MS = 60 * 1000;
function reloadOnce() {
  const lastReloadAt = Number(sessionStorage.getItem(RELOAD_GUARD_KEY)) || 0;
  if (lastReloadAt && Date.now() - lastReloadAt < RELOAD_GUARD_WINDOW_MS) {
    console.warn('[reloadOnce] already reloaded moments ago — preventing loop');
    return;
  }
  sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  window.location.reload();
}

Vue.config.errorHandler = (error, vm, info) => {
  console.error('[Vue errorHandler]', info, error);
  if (isChunkLoadError(error)) {
    // A new deploy changed chunk hashes — hard-reload to pick up the latest build.
    reloadOnce();
    return;
  }
  // For non-fatal render errors, dispatch a bus event so components can react
  // (e.g. show a toast) instead of crashing the whole sub-tree silently.
  window.WOOT &&
    window.WOOT.$emit('error', { error, info, source: 'vue-render' });
  // Sentry already captures this via the init above.
};

window.onerror = (message, source, lineno, colno, error) => {
  if (isChunkLoadError(error || message)) {
    reloadOnce();
    return true;
  }
  return false;
};

window.addEventListener('unhandledrejection', event => {
  if (isChunkLoadError(event.reason)) {
    reloadOnce();
    return;
  }
  // Log for debugging but don't show to the user — many promises are intentionally
  // caught elsewhere but still bubble as unhandled in some browsers.
  if (event.reason && event.reason.code !== 'ERR_CANCELED') {
    console.warn('[unhandledRejection]', event.reason);
  }
});

window.onload = () => {
  window.WOOT = new Vue({
    router,
    store,
    i18n: i18nConfig,
    components: { App },
    template: '<App/>',
  }).$mount('#app');
};

window.addEventListener('load', () => {
  window.playAudioAlert = () => {};
});
